<!-- eslint-disable vue/multi-word-component-names -->
<!-- echarts 控件主入口 -->
<template>
  <div :class="['echarts-base', className]" :style="{ height: computedHeight }">
    <div ref="containerRef" class="echarts-container" />
    <!-- 内置主题切换控件（可通过 showThemeToggle 关闭） -->
    <!-- <div v-if="showThemeToggle" class="theme-toggle">
			<el-switch v-model="isDark" active-text="暗" inactive-text="亮" @change="onThemeToggle" />
		</div> -->
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount, defineProps, defineEmits, defineExpose } from 'vue'
import type { PropType } from 'vue'
import type { EChartsOption } from 'echarts'
import { useECharts } from './index'
import type { EChartsBaseExpose } from './type/index.module'
import { darkTheme, lightTheme } from '@/utils/echarts-theme'
import { ElLoading } from 'element-plus'
import type { LoadingInstance } from 'element-plus/lib/components/loading/src/loading'

const props = defineProps({
  options: { type: Object as PropType<EChartsOption | null>, default: null },
  autoResize: { type: Boolean, default: true },
  debounce: { type: Number, default: 200 },
  height: { type: String, default: '320px' },
  theme: { type: [String, Object] as PropType<string | Record<string, any> | null>, default: 'dark' },
  initOpts: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  loading: { type: Boolean, default: false },
  optionMerge: { type: Boolean, default: true },
  className: { type: String, default: '' },
  useAutoTheme: { type: Boolean, default: true },
  showThemeToggle: { type: Boolean, default: true }
})

const emit = defineEmits(['ready', 'click', 'dblclick', 'mouseover', 'rendered', 'themeChange'])

const { root, init, getInstance, setOption, bindAutoResize, dispose, updateSeriesData, updateDataset } = useECharts()

const isDark = ref<boolean>(props.theme === 'dark' || typeof props.theme === 'object')

function applyInit(themeParam?: string | object | null) {
  init(themeParam ?? (isDark.value ? darkTheme : lightTheme), props.initOpts)
  if (props.options) {
    setOption(props.options, { notMerge: !props.optionMerge, lazyUpdate: false })
  }
  bindAutoResize(props.autoResize, props.debounce)
}

onMounted(async () => {
  await nextTick()
  applyInit(props.theme)
  const ins = getInstance()
  if (ins) {
    ins.on('click', (p) => emit('click', p))
    ins.on('dblclick', (p) => emit('dblclick', p))
    ins.on('mouseover', (p) => emit('mouseover', p))
  }
  // 如果 props.loading 为 true，则显示 loading（用 ElLoading）
  if (props.loading) showLoading()
  emit('ready', { instance: ins })
})

watch(
  () => props.options,
  (next) => {
    if (!next) return
    setTimeout(() => {
      setOption(next as EChartsOption, { notMerge: !props.optionMerge, lazyUpdate: false })
      emit('rendered')
    }, props.debounce)
  },
  { deep: true, immediate: true }
)

watch(
  () => props.autoResize,
  (v) => bindAutoResize(v, props.debounce)
)

// 当 props.loading 变化时自动控制
watch(
  () => props.loading,
  (v) => {
    if (v) showLoading()
    else hideLoading()
  }
)

// ElLoading 实例
const loadingInstance = ref<LoadingInstance | null>(null)

function showLoading(opts?: { text?: string; spinner?: string }) {
  // 如果已有 loading 实例先关闭
  hideLoading()
  try {
    loadingInstance.value = ElLoading.service({
      lock: true,
      text: opts?.text ?? '加载中...',
      spinner: opts?.spinner ?? undefined,
      background: 'rgba(0, 0, 0, 0.4)',
      // target: containerDom 使用默认全屏 overlay 会覆盖整个页面，若只想覆盖图表容器则可以设置 target
      target: (root.value as HTMLElement) ?? undefined
    }) as LoadingInstance
  } catch (e) {
    // 兼容性 fallback（某些 element-plus 版本 Loading.service 返回类型会不一致）
    loadingInstance.value = null
    // no-op
  }
}

function hideLoading() {
  if (loadingInstance.value && typeof loadingInstance.value.close === 'function') {
    loadingInstance.value.close()
    loadingInstance.value = null
  }
}

function onThemeToggle(val?: boolean) {
  isDark.value = typeof val === 'boolean' ? val : isDark.value
  dispose()
  applyInit(props.theme && typeof props.theme !== 'object' ? props.theme : isDark.value ? darkTheme : lightTheme)
  emit('themeChange', isDark.value ? 'dark' : 'light')
}

onBeforeUnmount(() => {
  hideLoading()
  dispose()
})

// 暴露 API（含 showLoading/hideLoading）
const exposed: EChartsBaseExpose = {
  getInstance,
  setOption: (opt, opts) => setOption(opt as EChartsOption, opts),
  resize: () => getInstance()?.resize(),
  dispose,
  updateSeriesData,
  updateDataset,
  showLoading,
  hideLoading
}
defineExpose(exposed)

// 返回给模板
const containerRef = root
const computedHeight = props.height
</script>

<style lang="scss" scoped>
@import '@/styles/echarts-theme.scss';

.echarts-base {
  position: relative;
  width: 100%;
  .echarts-container {
    width: 100%;
    height: 100%;
  }
  .theme-toggle {
    position: absolute;
    right: 8px;
    top: 8px;
    z-index: 30;
  }
}
</style>
